// services/api.js - VERSIÓN ACTUALIZADA CON SOPORTE MULTIMEDIA
import React, { useState } from 'react';

// Use a relative path so CRA dev server can proxy requests and avoid CORS during development.
// In production this should still resolve correctly when served from the same host.
const API_BASE_URL = '/sistemasms/backend/api';

// Función helper para debugging y manejo de errores
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  console.log(`🌐 API Request to: ${url}`);
  console.log(`📋 Options:`, options);
  
  try {
    const response = await fetch(url, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });
    
    console.log(`📡 Response status: ${response.status}`);
    
    const text = await response.text();
    console.log(`📄 Response (first 200 chars):`, text.substring(0, 200));
    
    let data;
    try {
      data = JSON.parse(text);
    } catch (parseError) {
      console.error('❌ JSON Parse Error:', parseError);
      console.error('📄 Full response:', text);
      throw new Error(`Invalid JSON response from ${endpoint}: ${text.substring(0, 100)}`);
    }
    
    if (!response.ok) {
      console.error(`❌ HTTP Error ${response.status}:`, data);
      throw new Error(`HTTP ${response.status}: ${data.message || 'Unknown error'}`);
    }
    
    console.log(`✅ Success:`, data);
    return { data };
    
  } catch (error) {
    console.error(`💥 API Request failed to ${endpoint}:`, error);
    throw error;
  }
};

// 🆕 FUNCIÓN ESPECIAL PARA SUBIR ARCHIVOS (SIN JSON)
const apiUpload = async (endpoint, formData) => {
  const url = `${API_BASE_URL}${endpoint}`;
  console.log(`📤 Upload Request to: ${url}`);
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      // NO agregar Content-Type para FormData - el browser lo hace automáticamente
      body: formData
    });
    
    console.log(`📡 Upload Response status: ${response.status}`);
    
    const text = await response.text();
    console.log(`📄 Upload Response:`, text);
    
    let data;
    try {
      data = JSON.parse(text);
    } catch (parseError) {
      console.error('❌ Upload JSON Parse Error:', parseError);
      throw new Error(`Invalid JSON response from upload: ${text.substring(0, 100)}`);
    }
    
    if (!response.ok) {
      console.error(`❌ Upload HTTP Error ${response.status}:`, data);
      throw new Error(`Upload HTTP ${response.status}: ${data.error || data.message || 'Unknown error'}`);
    }
    
    console.log(`✅ Upload Success:`, data);
    return { data };
    
  } catch (error) {
    console.error(`💥 Upload Request failed to ${endpoint}:`, error);
    throw error;
  }
};

// ==========================================
// APIs BASADAS EN TUS ARCHIVOS REALES
// ==========================================

// Auth API
export const authAPI = {
  login: (credentials) => {
    return apiRequest('/auth/login.php', {
      method: 'POST',
      body: JSON.stringify(credentials)
    });
  },
  
  logout: async () => {
    console.log('🚪 Logout (simulated)');
    return { data: { success: true } };
  }
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => {
    return apiRequest('/dashboard/stats.php');
  }
};

// Contacts API
export const contactsAPI = {
  list: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/contacts/list.php?${queryString}` : '/contacts/list.php';
    return apiRequest(endpoint);
  },
  
  import: (formData) => {
    console.log('📤 Importing contacts...');
    return fetch(`${API_BASE_URL}/contacts/import.php`, {
      method: 'POST',
      credentials: 'include',
      body: formData
    }).then(async response => {
      const text = await response.text();
      console.log(`Import response: ${text.substring(0, 200)}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      try {
        const data = JSON.parse(text);
        return { data };
      } catch (parseError) {
        console.error('Import JSON parse error:', parseError);
        throw new Error(`Invalid JSON response: ${text.substring(0, 100)}`);
      }
    });
  },
  
  create: (contactData) => {
    return apiRequest('/contacts/create.php', {
      method: 'POST',
      body: JSON.stringify(contactData)
    });
  },
  
  update: (contactId, contactData) => {
    return apiRequest('/contacts/update.php', {
      method: 'POST',
      body: JSON.stringify({ id: contactId, ...contactData })
    });
  },
  
  delete: (contactId) => {
    return apiRequest('/contacts/delete.php', {
      method: 'POST',
      body: JSON.stringify({ id: contactId })
    });
  }
};

// Executives / Usuarios Ejecutivos API
// Intenta usar endpoints reales en backend (/settings/executives*.php) y si no
// están disponibles usa un fallback en localStorage para permitir pruebas UI.
export const executivesAPI = {
  list: async () => {
    try {
      return await apiRequest('/settings/executives/list.php');
    } catch (error) {
      console.warn('Executives API not available, using localStorage fallback');
      const raw = localStorage.getItem('executives_list');
      const data = raw ? JSON.parse(raw) : [];
      return { data: { executives: data } };
    }
  },

  create: async (payload) => {
    try {
      return await apiRequest('/settings/executives/create.php', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
    } catch (error) {
      // Fallback: guardar en localStorage
      const raw = localStorage.getItem('executives_list');
      const list = raw ? JSON.parse(raw) : [];
      const id = Date.now();
      const newItem = { id, ...payload, enabled: payload.enabled !== false };
      list.push(newItem);
      localStorage.setItem('executives_list', JSON.stringify(list));
      return { data: { success: true, executive: newItem } };
    }
  },

  update: async (id, payload) => {
    try {
      return await apiRequest('/settings/executives/update.php', {
        method: 'POST',
        body: JSON.stringify({ id, ...payload })
      });
    } catch (error) {
      const raw = localStorage.getItem('executives_list');
      const list = raw ? JSON.parse(raw) : [];
      const idx = list.findIndex(i => i.id === id);
      if (idx !== -1) {
        list[idx] = { ...list[idx], ...payload };
        localStorage.setItem('executives_list', JSON.stringify(list));
        return { data: { success: true, executive: list[idx] } };
      }
      throw error;
    }
  },

  delete: async (id) => {
    try {
      return await apiRequest('/settings/executives/delete.php', {
        method: 'POST',
        body: JSON.stringify({ id })
      });
    } catch (error) {
      const raw = localStorage.getItem('executives_list');
      const list = raw ? JSON.parse(raw) : [];
      const filtered = list.filter(i => i.id !== id);
      localStorage.setItem('executives_list', JSON.stringify(filtered));
      return { data: { success: true } };
    }
  },

  toggleEnabled: async (id, enabled) => {
    try {
      return await apiRequest('/settings/executives/toggle.php', {
        method: 'POST',
        body: JSON.stringify({ id, enabled })
      });
    } catch (error) {
      const raw = localStorage.getItem('executives_list');
      const list = raw ? JSON.parse(raw) : [];
      const idx = list.findIndex(i => i.id === id);
      if (idx !== -1) {
        list[idx].enabled = !!enabled;
        localStorage.setItem('executives_list', JSON.stringify(list));
        return { data: { success: true, executive: list[idx] } };
      }
      throw error;
    }
  }
};

// 🆕 CAMPAIGNS API ACTUALIZADA CON SOPORTE MULTIMEDIA
export const campaignsAPI = {
  list: () => {
    return apiRequest('/campaigns/list.php');
  },
  
  // ✨ CREAR CAMPAÑA (ahora con soporte multimedia)
  create: (campaignData) => {
    console.log('📝 Creating campaign:', campaignData);
    return apiRequest('/campaigns/create.php', {
      method: 'POST',
      body: JSON.stringify(campaignData)
    });
  },
  
  // ✨ SUBIR IMAGEN PARA CAMPAÑA
  uploadImage: (imageFile) => {
    console.log('📸 Uploading campaign image:', imageFile.name);
    const formData = new FormData();
    formData.append('image', imageFile);
    return apiUpload('/upload/image.php', formData);
  },
  
  // ✨ ENVIAR CAMPAÑA (con soporte multimedia)
  send: (campaignId, options = {}) => {
    console.log('🚀 Sending campaign:', campaignId, 'with options:', options);
    return apiRequest('/campaigns/send.php', {
      method: 'POST',
      body: JSON.stringify({ 
        campaign_id: campaignId,
        include_media: true,
        ...options 
      })
    });
  },
  
  delete: (campaignId) => {
    return apiRequest('/campaigns/delete.php', {
      method: 'POST',
      body: JSON.stringify({ id: campaignId })
    });
  },

  getStats: (campaignId) => {
    return apiRequest(`/campaigns/stats.php?campaign_id=${campaignId}`);
  },

  // 🆕 OBTENER DETALLES DE CAMPAÑA (incluye info multimedia)
  getDetails: (campaignId) => {
    return apiRequest(`/campaigns/details.php?id=${campaignId}`);
  }
};

// 🆕 WHATSAPP API ACTUALIZADA
export const whatsappAPI = {
  // Verificar estado de WhatsApp
  getStatus: () => {
    return apiRequest('/whatsapp/status.php');
  },
  
  // ✨ ENVIAR CAMPAÑA (streaming con soporte multimedia)
  sendCampaign: (campaignId) => {
    console.log('📱 Sending WhatsApp campaign:', campaignId);
    
    // Retornar la respuesta de streaming directamente
    return fetch(`${API_BASE_URL}/whatsapp/send-campaign.php`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ 
        campaign_id: campaignId,
        include_media: true // 🆕 Indicar que incluya multimedia
      })
    });
  },
  
  // ✨ ENVIAR MENSAJE INDIVIDUAL (con soporte multimedia)
  sendMessage: (phone, message, mediaData = null) => {
    const payload = {
      phone: phone,
      message: message
    };
    
    // 🆕 Agregar datos multimedia si existen
    if (mediaData) {
      payload.media = {
        type: mediaData.type,
        url: mediaData.url,
        caption: mediaData.caption
      };
    }
    
    return apiRequest('/whatsapp/send-message.php', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },
  
  // Obtener logs de mensajes
  getMessageLogs: (campaignId) => {
    return apiRequest(`/whatsapp/message-logs.php?campaign_id=${campaignId}`);
  },
  
  // 🆕 OBTENER RESPUESTAS RECIBIDAS
  getResponses: (campaignId) => {
    return apiRequest(`/whatsapp/responses.php?campaign_id=${campaignId}`);
  }
};

// Settings API
export const settingsAPI = {
  getSettings: () => {
    return apiRequest('/settings/get-settings.php');
  },
  
  updateSettings: (settingsData) => {
    return apiRequest('/settings/update-settings.php', {
      method: 'POST',
      body: JSON.stringify(settingsData)
    });
  },
  
  getEmpresas: () => {
    return apiRequest('/settings/get-empresas.php');
  }
};

// 🆕 ANALYTICS API (que faltaba)
export const analyticsAPI = {
  getAnalytics: (period = '30') => {
    return apiRequest(`/analytics/analytics.php?period=${period}`);
  }
};

// 🆕 UPLOAD API (para manejo general de archivos)
export const uploadAPI = {
  // Subir imagen para campañas
  image: (imageFile) => {
    console.log('🖼️ Uploading image:', imageFile.name, 'Size:', imageFile.size);
    const formData = new FormData();
    formData.append('image', imageFile);
    return apiUpload('/upload/image.php', formData);
  },
  
  // Futuro: subir documentos, videos, etc.
  document: (documentFile) => {
    console.log('📄 Uploading document:', documentFile.name);
    const formData = new FormData();
    formData.append('document', documentFile);
    return apiUpload('/upload/document.php', formData);
  }
};

// ==========================================
// FUNCIONES DE DEBUGGING Y TESTING
// ==========================================

export const testDirectConnectivity = async () => {
  console.log('🔍 Testing direct connectivity...');
  
  try {
    const response = await fetch(`${API_BASE_URL}/test.php`, {
      credentials: 'include'
    });
    
    const text = await response.text();
    console.log(`📡 Direct test response: ${text}`);
    
    return { success: true, response: text };
  } catch (error) {
    console.error('❌ Direct connectivity failed:', error);
    return { success: false, error: error.message };
  }
};

export const testRealAPIs = async () => {
  console.log('🧪 Testing real APIs...');
  
  const tests = [
    { name: 'Dashboard Stats', api: () => dashboardAPI.getStats() },
    { name: 'Contacts List', api: () => contactsAPI.list() },
    { name: 'Campaigns List', api: () => campaignsAPI.list() },
    { name: 'WhatsApp Status', api: () => whatsappAPI.getStatus() }
  ];
  
  const results = {};
  
  for (const test of tests) {
    try {
      console.log(`🔬 Testing ${test.name}...`);
      const result = await test.api();
      results[test.name] = { success: true, data: result.data };
      console.log(`✅ ${test.name} OK`);
    } catch (error) {
      results[test.name] = { success: false, error: error.message };
      console.log(`❌ ${test.name} FAILED:`, error.message);
    }
  }
  
  return results;
};

export const initTestSession = async () => {
  console.log('🔐 Initializing test session...');
  
  try {
    const testCredentials = {
      username: 'test@test.com',
      password: 'test123'
    };
    
    const response = await authAPI.login(testCredentials);
    console.log('✅ Test session initialized:', response.data);
    return response.data;
  } catch (error) {
    console.log('⚠️ Login failed, simulating session...');
    return { success: true, simulated: true };
  }
};

// ==========================================
// HOOK PERSONALIZADO PARA REACT
// ==========================================
export const useAPIWithFallback = (apiCall, fallbackData = null) => {
  const [data, setData] = useState(fallbackData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const execute = async (...args) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiCall(...args);
      setData(response.data);
      return response.data;
    } catch (err) {
      console.error('API Error:', err);
      setError(err);
      
      console.log('📝 Usando datos de fallback...');
      setData(fallbackData);
      return fallbackData;
    } finally {
      setLoading(false);
    }
  };
  
  return { data, loading, error, execute };
};

// ==========================================
// COMPONENTE DE DEBUG PARA REACT
// ==========================================
export const DebugPanel = ({ onTest }) => {
  const [showDebug, setShowDebug] = useState(false);
  
  if (!showDebug) {
    return (
      <button 
        onClick={() => setShowDebug(true)}
        className="fixed bottom-4 right-4 bg-red-600 text-white px-3 py-2 rounded-lg text-sm z-50"
      >
        Debug 🔧
      </button>
    );
  }
  
  return (
    <div className="fixed bottom-4 right-4 bg-white border-2 border-red-500 rounded-lg p-4 shadow-lg max-w-sm z-50">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-bold text-red-600">Debug Panel</h3>
        <button onClick={() => setShowDebug(false)} className="text-gray-500">✕</button>
      </div>
      
      <div className="space-y-2">
        <button 
          onClick={() => testRealAPIs()}
          className="w-full bg-blue-600 text-white px-3 py-2 rounded text-sm"
        >
          Probar APIs Reales
        </button>
        
        <button 
          onClick={() => testDirectConnectivity()}
          className="w-full bg-green-600 text-white px-3 py-2 rounded text-sm"
        >
          Test Conectividad
        </button>

        <button 
          onClick={() => initTestSession()}
          className="w-full bg-purple-600 text-white px-3 py-2 rounded text-sm"
        >
          Inicializar Sesión
        </button>
        
        {/* 🆕 BOTÓN PARA PROBAR SUBIDA DE IMÁGENES */}
        <button 
          onClick={() => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.onchange = async (e) => {
              const file = e.target.files[0];
              if (file) {
                try {
                  console.log('🖼️ Testing image upload...');
                  const result = await uploadAPI.image(file);
                  console.log('✅ Image upload test result:', result);
                  alert('✅ Imagen subida exitosamente: ' + result.data.url);
                } catch (error) {
                  console.error('❌ Image upload test failed:', error);
                  alert('❌ Error subiendo imagen: ' + error.message);
                }
              }
            };
            input.click();
          }}
          className="w-full bg-orange-600 text-white px-3 py-2 rounded text-sm"
        >
          Test Subir Imagen
        </button>
        
        <button 
          onClick={() => {
            console.log('📊 Estado actual de la aplicación');
            console.log('🌐 API Base URL:', API_BASE_URL);
            console.log('🎯 APIs disponibles:', Object.keys({
              authAPI, dashboardAPI, contactsAPI, campaignsAPI, 
              whatsappAPI, settingsAPI, uploadAPI
            }));
          }}
          className="w-full bg-gray-600 text-white px-3 py-2 rounded text-sm"
        >
          Log Estado App
        </button>
      </div>
    </div>
  );
};

// Exportar todas las APIs
export default {
  auth: authAPI,
  dashboard: dashboardAPI,
  contacts: contactsAPI,
  campaigns: campaignsAPI,
  whatsapp: whatsappAPI,
  settings: settingsAPI,
  upload: uploadAPI,
  analytics: analyticsAPI  // 🆕 Agregado
};